import { API_BASE } from '../../../config/api';

// Server pagination envelope, shared by every paged list endpoint
// (GET /api/artist?page=1&per_page=50 and friends). Mirrors the Go
// pagination.Envelope shape in www/api/pagination.
export interface PagedEnvelope<T> {
    items: T[];
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
}

export function isPagedEnvelope<T>(value: unknown): value is PagedEnvelope<T> {
    if (typeof value !== 'object' || value === null) return false;
    const candidate = value as Record<string, unknown>;
    return Array.isArray(candidate.items) && typeof candidate.total === 'number' && typeof candidate.page === 'number';
}

// fetchPaged performs one paged GET and unwraps the envelope. Failure
// falls back to an empty envelope, matching the plain list fetchers'
// "log and return []" behaviour.
// A filter value is either one string (?q=love) or a list of values that
// become repeated params (?genre=a&genre=b). Empty strings and empty
// arrays are dropped so URLs stay clean.
export type FilterValue = string | string[];

export function buildPagedUrl(endpoint: string, page: number, perPage: number, filters?: Record<string, FilterValue>): string {
    const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
    if (filters) {
        for (const [k, v] of Object.entries(filters)) {
            if (Array.isArray(v)) {
                for (const item of v) {
                    if (item.trim() !== '') params.append(k, item.trim());
                }
            } else if (v.trim() !== '') {
                params.set(k, v.trim());
            }
        }
    }
    return `${API_BASE}${endpoint}?${params.toString()}`;
}

export async function fetchPaged<T>(endpoint: string, page: number, perPage: number, filters?: Record<string, FilterValue>): Promise<PagedEnvelope<T>> {
    try {
        const response = await fetch(buildPagedUrl(endpoint, page, perPage, filters));
        const result = await response.json();
        if (!response.ok) {
            throw new Error(`Error fetching ${endpoint}: ${result.message || response.statusText}`);
        }
        if (isPagedEnvelope<T>(result)) {
            return result;
        }
        return { items: [], page: 1, per_page: perPage, total: 0, total_pages: 1 };
    } catch (error) {
        console.error(error);
        return { items: [], page: 1, per_page: perPage, total: 0, total_pages: 1 };
    }
}
